// useBackstage — the hand-written per-domain hooks over the generated client for the Antenna
// backstage manager: the members-only perks shelf. Lives under app/hooks/ (the lint-exempt
// wrapper dir), built on useBandQuery/useBandMutation so the key is ['band', bandId, 'antenna', 'backstage'].
//
// Mutation posture: create/delete are confirm-then-repaint — NEVER optimistic. Each invalidates the
// list on server success so the manager repaints via a GET refetch (no phantom rows).
import { useQueryClient } from "@tanstack/react-query";
import type { components } from "@cntrl/api-client";
import { useApp } from "../appContext.js";
import { useBandContext } from "../bandContext.js";
import { useBandQuery, useBandMutation } from "./useBandQuery.js";
import { ApiError } from "./apiError.js";

type BackstageItemView = components["schemas"]["BackstageItemView"];
type CreateBackstageItemRequest = components["schemas"]["CreateBackstageItemRequest"];

/** The backstage-items key. */
export const backstageKey = (bandId: string) => ["band", bandId, "antenna", "backstage"] as const;

/**
 * GET the backstage shelf. `antenna:read`; a seat without it 403s → the query's error state (the
 * client mirror of the server deny). Born-blank returns an empty list → the designed empty state.
 */
export function useBackstageItems() {
  const { api } = useApp();
  return useBandQuery<BackstageItemView[]>(
    ["antenna", "backstage"],
    async (bandId) => {
      const { data, error, response } = await api.GET(
        "/api/v1/bands/{bandId}/antenna/backstage",
        { params: { path: { bandId } } },
      );
      if (!response.ok || !data) throw new ApiError(response.status, error);
      return data.data ?? [];
    },
    { staleTime: 15_000 },
  );
}

/** Add a backstage item (POST → 201 | 400). Not optimistic; invalidates the list on success.*/
export function useCreateBackstageItem() {
  const { api } = useApp();
  const { bandId } = useBandContext();
  const qc = useQueryClient();

  return useBandMutation<BackstageItemView, CreateBackstageItemRequest>(
    async (band, body) => {
      const { data, error, response } = await api.POST(
        "/api/v1/bands/{bandId}/antenna/backstage",
        { params: { path: { bandId: band } }, body },
      );
      if (!response.ok || !data?.data) throw new ApiError(response.status, error);
      return data.data;
    },
    { onSuccess: () => void qc.invalidateQueries({ queryKey: backstageKey(bandId) }) },
  );
}

/** Remove a backstage item (DELETE /backstage/{itemId} → 204 | 404). Not optimistic; invalidates.*/
export function useDeleteBackstageItem() {
  const { api } = useApp();
  const { bandId } = useBandContext();
  const qc = useQueryClient();

  return useBandMutation<void, string>(
    async (band, itemId) => {
      const { error, response } = await api.DELETE(
        "/api/v1/bands/{bandId}/antenna/backstage/{itemId}",
        { params: { path: { bandId: band, itemId } } },
      );
      if (!response.ok) throw new ApiError(response.status, error);
    },
    { onSuccess: () => void qc.invalidateQueries({ queryKey: backstageKey(bandId) }) },
  );
}
